import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  ScreenShare,
  StopScreenShare,
  Chat,
  ExitToApp,
} from '@mui/icons-material';
import { HMSRoomProvider, useHMSActions, useHMSStore, selectPeers, selectIsConnectedToRoom, selectLocalPeer, selectIsLocalAudioEnabled, selectIsLocalVideoEnabled, selectIsLocalScreenShared, HMSPeer, selectCameraStreamByPeerID } from '@100mslive/hms-video-react';

// Safe VideoTile Component that only renders when HMS context is truly ready
const SafeVideoTile: React.FC<{ peer: HMSPeer; objectFit: 'contain' | 'cover' }> = ({ peer, objectFit }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const localPeer = useHMSStore(selectLocalPeer);
  const hmsActions = useHMSActions();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cameraTrack = useHMSStore(selectCameraStreamByPeerID(peer.id));
  const trackId = cameraTrack?.id as string | undefined;
  // Track video enabled state to detect toggles (HMS reuses track IDs)
  const localVideoEnabled = useHMSStore(selectIsLocalVideoEnabled);
  const isVideoEnabled = peer.isLocal ? localVideoEnabled : peer.videoTrack !== undefined;

  useEffect(() => {
    // Wait for HMS to be connected AND local peer to be ready, then add extra delay
    if (isConnected && localPeer && localPeer.id && peer && peer.id) {
      const timer = setTimeout(() => {
        setShouldRender(true);
      }, 800);

      return () => clearTimeout(timer);
    } else {
      setShouldRender(false);
    }
  }, [isConnected, localPeer, peer]);

  useEffect(() => {
    console.log(`[VideoTile] Effect triggered for peer ${peer.id}, shouldRender: ${shouldRender}, trackId: ${trackId}, isVideoEnabled: ${isVideoEnabled}, peer.isLocal: ${peer.isLocal}`);
    
    if (!shouldRender || !trackId || !isVideoEnabled) {
      console.log(`[VideoTile] Skipping attach - shouldRender: ${shouldRender}, trackId: ${trackId}, isVideoEnabled: ${isVideoEnabled}`);
      return;
    }
    
    const videoElement = videoRef.current;
    if (!videoElement) {
      console.log(`[VideoTile] No video element available`);
      return;
    }
    
    console.log(`[VideoTile] Attaching track ${trackId} for peer ${peer.id}, peer.isLocal: ${peer.isLocal}`);
    let cancelled = false;
    
    const attach = async (attempt = 1) => {
      try {
        if (cancelled || !videoElement) return;
        
        // Detach any existing track first
        const existingSrcObject = videoElement.srcObject;
        if (existingSrcObject) {
          console.log(`[VideoTile] Clearing existing srcObject before attaching new track`);
          videoElement.srcObject = null;
        }
        
        // Small delay to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 100));
        
        await hmsActions.attachVideo(trackId, videoElement);
        console.log(`[VideoTile] Successfully attached track ${trackId}`);
        
        // Force video element to play
        await videoElement.play();
        console.log(`[VideoTile] Video playing successfully`);
      } catch (error) {
        console.warn(`[VideoTile] Failed to attach track ${trackId} (attempt ${attempt}):`, error);
        // Retry a couple of times to handle race conditions
        if (attempt < 3 && !cancelled) {
          setTimeout(() => attach(attempt + 1), 300);
        }
      }
    };
    
    attach();
    
    return () => {
      console.log(`[VideoTile] Cleanup for track ${trackId}, peer ${peer.id}`);
      cancelled = true;
      if (videoElement && trackId) {
        console.log(`[VideoTile] Detaching track ${trackId} for peer ${peer.id}`);
        hmsActions.detachVideo(trackId, videoElement).catch(() => {});
        // Don't stop tracks here - HMS manages track lifecycle
        // Only clear srcObject to prevent black screen issues
        videoElement.srcObject = null;
      }
    };
  }, [shouldRender, trackId, isVideoEnabled, hmsActions, peer.id, peer.isLocal]);

  if (!shouldRender || !peer || !peer.id || !isConnected) {
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={20} sx={{ color: 'white' }} />
      </Box>
    );
  }

  // Show loading if no track yet
  if (!trackId) {
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: 'white' }}>Waiting for video...</Typography>
      </Box>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      muted={peer.isLocal}
      playsInline
      style={{ width: '100%', height: '100%', objectFit, backgroundColor: '#000' }}
    />
  );
};

// Error Boundary Component for HMS Components (unused but kept for future use)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
class HMSErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('HMS Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Video component error
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please refresh the page to try again
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

interface HMSMeetingRoomProps {
  token: string;
  roomCode: string;
  userName: string;
  userRole: string;
  onLeave: () => void;
}

const HMSMeetingRoomInner: React.FC<HMSMeetingRoomProps> = ({
  token,
  roomCode,
  userName,
  userRole,
  onLeave,
}) => {
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const peers = useHMSStore(selectPeers);
  const localPeer = useHMSStore(selectLocalPeer);
  const localAudioEnabled = useHMSStore(selectIsLocalAudioEnabled);
  const localVideoEnabled = useHMSStore(selectIsLocalVideoEnabled);
  const localScreenShared = useHMSStore(selectIsLocalScreenShared);
  const hasLeftRef = useRef(false);
  const cleanupInProgressRef = useRef(false);
  const leaveButtonClickedRef = useRef(false);

  // Simplified HMS context readiness check
  const isHMSContextReady = isConnected && peers && localPeer !== null && peers.length > 0 && localPeer?.id;

  const [chatOpen, setChatOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Shared cleanup function to stop all media tracks
  const performCleanup = async (source: string) => {
    if (cleanupInProgressRef.current) {
      console.log(`[${source}] Cleanup already in progress, skipping`);
      return;
    }
    cleanupInProgressRef.current = true;
    
    console.log(`[${source}] === Starting cleanup ===`);
    
    // Step 1: Disable all media via HMS
    console.log(`[${source}] Step 1: Disabling HMS media...`);
    try { 
      await hmsActions.setScreenShareEnabled(false); 
      console.log(`[${source}] Screen share disabled`);
    } catch (e) {
      console.error(`[${source}] Error disabling screen share:`, e);
    }
    
    try { 
      await hmsActions.setLocalVideoEnabled(false); 
      console.log(`[${source}] Video disabled via HMS`);
    } catch (e) {
      console.error(`[${source}] Error disabling video:`, e);
    }
    
    try { 
      await hmsActions.setLocalAudioEnabled(false); 
      console.log(`[${source}] Audio disabled via HMS`);
    } catch (e) {
      console.error(`[${source}] Error disabling audio:`, e);
    }
    
    // Wait for HMS to process the disable commands
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Step 2: Leave the HMS room
    console.log(`[${source}] Step 2: Leaving HMS room...`);
    try {
      if (isConnected) {
        await hmsActions.leave();
        console.log(`[${source}] Successfully left HMS room`);
      }
    } catch (e) {
      console.error(`[${source}] Error leaving HMS room:`, e);
    }
    
    // Wait for leave to complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Step 3: Stop all video element streams
    console.log(`[${source}] Step 3: Stopping video element streams...`);
    try {
      const videos = document.querySelectorAll('video');
      console.log(`[${source}] Found ${videos.length} video elements`);
      videos.forEach((videoEl) => {
        const stream = (videoEl as HTMLVideoElement).srcObject as MediaStream | null;
        if (stream) {
          const tracks = stream.getTracks();
          console.log(`[${source}] Stopping ${tracks.length} tracks from video element`);
          tracks.forEach(track => {
            console.log(`[${source}] Stopping track: ${track.kind} - ${track.label} (state: ${track.readyState})`);
            track.stop();
          });
          (videoEl as HTMLVideoElement).srcObject = null;
        }
      });
    } catch (e) {
      console.error(`[${source}] Error stopping video element streams:`, e);
    }
    
    // Step 4: Get and stop all active media tracks from navigator
    console.log(`[${source}] Step 4: Stopping all navigator media tracks...`);
    try {
      if (navigator.mediaDevices && (navigator.mediaDevices as any).getUserMedia) {
        const allVideos = document.querySelectorAll('video');
        allVideos.forEach((video) => {
          const stream = (video as HTMLVideoElement).srcObject as MediaStream;
          if (stream && stream.getTracks) {
            stream.getTracks().forEach(track => {
              if (track.readyState === 'live') {
                console.log(`[${source}] Force stopping live track: ${track.kind} - ${track.label}`);
                track.stop();
              }
            });
          }
        });
      }
    } catch (e) {
      console.error(`[${source}] Error stopping navigator tracks:`, e);
    }
    
    console.log(`[${source}] === Cleanup complete ===`);
  };

  // Monitor HMS context readiness - simplified since HMSContextProvider handles initialization
  useEffect(() => {
    if (isConnected && peers && localPeer && peers.length > 0 && localPeer?.id) {
      // Add a delay to ensure HMS context is fully ready
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, peers, localPeer]);

  useEffect(() => {
    // Reset flags on mount
    hasLeftRef.current = false;
    leaveButtonClickedRef.current = false;
    cleanupInProgressRef.current = false;
    
    const joinRoom = async () => {
      try {
        console.log('=== HMS JOIN DEBUG ===');
        console.log('Token:', token);
        console.log('Room code:', roomCode);
        console.log('User:', userName);
        console.log('User role:', userRole);
        
        // Decode token to see what's inside
        try {
          const tokenParts = token.split('.');
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('Token payload:', payload);
          console.log('Token room_code:', payload.room_code);
          console.log('Expected room_code:', roomCode);
          console.log('Room codes match:', payload.room_code === roomCode);
        } catch (e) {
          console.error('Failed to decode token:', e);
        }
        
        console.log('=== END DEBUG ===');
        
        // Request camera and microphone permissions before joining
        try {
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            const stream = await navigator.mediaDevices.getUserMedia({ 
              video: true, 
              audio: true 
            });
            // Stop the stream immediately - we just needed permission
            stream.getTracks().forEach(track => track.stop());
            console.log('Camera and microphone permissions granted');
          }
        } catch (permError: any) {
          console.error('Permission error:', permError);
          if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
            setError('Camera and microphone permissions are required. Please allow access and refresh the page.');
            return;
          } else if (permError.name === 'NotFoundError' || permError.name === 'DevicesNotFoundError') {
            setError('No camera or microphone found. Please connect a device and refresh the page.');
            return;
          }
        }
        
        await hmsActions.join({
          userName,
          authToken: token,
          settings: {
            // Start muted to avoid publishing before peer is fully ready
            isAudioMuted: true,
            isVideoMuted: true,
          },
        });
        console.log('Successfully joined 100ms room!');
      } catch (e: any) {
        console.error('Failed to join 100ms room:', e);
        if (e.message && e.message.includes('permission')) {
          setError('Camera or microphone permission denied. Please allow access in your browser settings and refresh the page.');
        } else {
          setError(`Failed to join meeting room: ${e.message || 'Unknown error'}`);
        }
      }
    };

    if (token && userName && !isConnected) {
      joinRoom();
    }

    return () => {
      // Only run cleanup in useEffect if user didn't click Leave button
      if (!leaveButtonClickedRef.current && !hasLeftRef.current) {
        console.log('[UseEffect] Component unmounting, running cleanup');
        hasLeftRef.current = true;
        performCleanup('UseEffect').catch(e => 
          console.error('[UseEffect] Cleanup error:', e)
        );
      } else {
        console.log('[UseEffect] Skipping cleanup - leaveButtonClicked:', leaveButtonClickedRef.current, 'hasLeft:', hasLeftRef.current);
      }
    };
  }, [token, userName, isConnected, roomCode, userRole, hmsActions]);

  // After connection and initialization, enable audio/video once
  const mediaEnabledRef = useRef(false);
  useEffect(() => {
    const enableMedia = async () => {
      if (mediaEnabledRef.current) return;
      if (!isConnected || !localPeer || !localPeer.id || !isInitialized) return;
      try {
        mediaEnabledRef.current = true;
        console.log('[EnableMedia] Enabling audio and video...');
        // Enable audio first, then video; add small delays to avoid race conditions
        await hmsActions.setLocalAudioEnabled(true);
        console.log('[EnableMedia] Audio enabled');
        await new Promise(r => setTimeout(r, 250));
        await hmsActions.setLocalVideoEnabled(true);
        console.log('[EnableMedia] Video enabled');
      } catch (err) {
        console.error('Error enabling local media:', err);
        // Do not surface as fatal; user can still toggle manually
      }
    };
    enableMedia();
  }, [isConnected, localPeer, isInitialized, hmsActions]);

  const toggleAudio = async () => {
    try {
      await hmsActions.setLocalAudioEnabled(!localAudioEnabled);
    } catch (error) {
      console.error('Error toggling audio:', error);
    }
  };

  const toggleVideo = async () => {
    try {
      const newState = !localVideoEnabled;
      console.log(`[toggleVideo] Changing video from ${localVideoEnabled} to ${newState}`);
      await hmsActions.setLocalVideoEnabled(newState);
      console.log(`[toggleVideo] Video successfully changed to ${newState}`);
    } catch (error: any) {
      console.error('[toggleVideo] Error toggling video:', error);
      if (error.message && error.message.includes('permission')) {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.message) {
        setError(`Failed to toggle video: ${error.message}`);
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (localScreenShared) {
        await hmsActions.setScreenShareEnabled(false);
      } else {
        // Check screen share permissions before enabling
        try {
          // Note: getDisplayMedia requires user interaction, so we just try to enable
          // The browser will prompt the user
          await hmsActions.setScreenShareEnabled(true);
        } catch (shareError: any) {
          if (shareError.message && shareError.message.includes('permission')) {
            setError('Screen share permission denied. Please allow screen sharing when prompted.');
          } else if (shareError.message && shareError.message.includes('NotAllowedError')) {
            setError('Screen share was cancelled or denied. Please try again.');
          } else {
            throw shareError;
          }
        }
      }
    } catch (error: any) {
      console.error('Error toggling screen share:', error);
      // Don't show error for screen share - it's not critical
      // The error is already logged and the user can try again
    }
  };

  const sendMessage = async () => {
    if (message.trim()) {
      try {
        await hmsActions.sendBroadcastMessage(message);
        setMessages(prev => [...prev, { 
          sender: userName, 
          text: message, 
          time: new Date().toLocaleTimeString() 
        }]);
        setMessage('');
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const leaveRoom = async () => {
    console.log('[leaveRoom] Called, hasLeftRef:', hasLeftRef.current, 'leaveButtonClicked:', leaveButtonClickedRef.current);
    
    if (leaveButtonClickedRef.current) {
      console.log('[leaveRoom] Leave already in progress, ignoring');
      return;
    }
    
    leaveButtonClickedRef.current = true;
    hasLeftRef.current = true;
    
    try {
      await performCleanup('LeaveButton');
    } catch (error) {
      console.error('[LeaveButton] Fatal error in leaveRoom:', error);
    } finally {
      // Small delay before calling onLeave to ensure cleanup is complete
      setTimeout(() => {
        console.log('[LeaveButton] Calling onLeave callback');
        onLeave();
      }, 200);
    }
  };

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={onLeave}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!isConnected || !isInitialized || !isHMSContextReady || !localPeer || !localPeer?.id) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f2f5' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: '#666666' }}>
          {!isConnected ? 'Connecting to meeting...' : 'Initializing video...'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: '#999999' }}>
          {!isConnected && 'Joining room...'}
          {isConnected && !localPeer && 'Setting up local peer...'}
          {isConnected && localPeer && !localPeer.id && 'Initializing peer ID...'}
          {isConnected && localPeer && localPeer.id && !isHMSContextReady && 'Preparing video context...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f0f2f5' }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#ffffff',
          borderRadius: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#081122' }}>
          Meeting: {roomCode}
        </Typography>
        <Box>
          <Tooltip title="Toggle Audio">
            <IconButton onClick={toggleAudio} color={localAudioEnabled ? 'primary' : 'default'}>
              {localAudioEnabled ? <Mic /> : <MicOff />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Video">
            <IconButton onClick={toggleVideo} color={localVideoEnabled ? 'primary' : 'default'}>
              {localVideoEnabled ? <Videocam /> : <VideocamOff />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Screen Share">
            <IconButton onClick={toggleScreenShare} color={localScreenShared ? 'primary' : 'default'}>
              {localScreenShared ? <StopScreenShare /> : <ScreenShare />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Chat">
            <IconButton onClick={() => setChatOpen(true)}>
              <Chat />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            startIcon={<ExitToApp />}
            onClick={leaveRoom}
            sx={{ ml: 2 }}
          >
            Leave
          </Button>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', gap: 3 }}>
        {/* Main Video Area */}
        <Paper sx={{ flexGrow: 1, borderRadius: 3, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#333', position: 'relative' }}>
          {peers && peers.length > 0 && isHMSContextReady && isInitialized ? (
            <>
              {/* Main video - show the first peer or local peer */}
              <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                {localPeer && localPeer?.id ? (
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <SafeVideoTile
                      peer={localPeer}
                      objectFit="contain"
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', height: '100%', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" color="textSecondary">No Video Available</Typography>
                  </Box>
                )}
                
                {/* Local video overlay */}
                {localPeer && localPeer?.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      width: 200,
                      height: 150,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid #2196f3',
                      backgroundColor: '#2a2a2a',
                    }}
                  >
                    <SafeVideoTile
                      peer={localPeer}
                      objectFit="cover"
                    />
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                🎥 Video Meeting
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Room: {roomCode}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {!isHMSContextReady ? 'Initializing video...' : 'Waiting for participants...'}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Sidebar for participants */}
        {peers && peers.length > 1 && isHMSContextReady && isInitialized && (
          <Paper sx={{ width: 250, borderRadius: 3, p: 2, bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#081122' }}>
              Participants ({peers.length})
            </Typography>
            <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {peers.filter(peer => peer && peer.id).map((peer) => (
                <Paper key={peer.id || `peer-${Math.random()}`} sx={{ p: 1, bgcolor: '#f9f9f9', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: '60px', height: '45px', borderRadius: '4px', overflow: 'hidden' }}>
                    {peer && peer.id && isHMSContextReady && isInitialized ? (
                      <SafeVideoTile
                        peer={peer}
                        objectFit="cover"
                      />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption">No Video</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {peer.name || 'Unknown User'} {peer.isLocal ? '(You)' : ''}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {peer.roleName || 'participant'}
                    </Typography>
                  </Box>
                  <Tooltip title={peer.audioTrack ? 'Unmuted' : 'Muted'}>
                    {peer.audioTrack ? <Mic /> : <MicOff color="error" />}
                  </Tooltip>
                </Paper>
              ))}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Chat Dialog */}
      <Dialog open={chatOpen} onClose={() => setChatOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chat</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: 400 }}>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
            {messages.map((msg, index) => (
              <Box key={index} sx={{ mb: 1, textAlign: msg.sender === userName ? 'right' : 'left' }}>
                <Chip
                  label={`${msg.sender || 'Unknown'}: ${msg.text || ''}`}
                  color={msg.sender === userName ? 'primary' : 'default'}
                  sx={{ maxWidth: '80%' }}
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                  {msg.time || 'Unknown time'}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  sendMessage();
                }
              }}
            />
            <Button variant="contained" onClick={sendMessage}>
              Send
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Wrapper component that includes HMSRoomProvider
// Using React.createElement to bypass TypeScript children issue
const HMSMeetingRoom: React.FC<HMSMeetingRoomProps> = (props) => {
  return React.createElement(
    HMSRoomProvider,
    {},
    React.createElement(HMSMeetingRoomInner, props)
  );
};

export default HMSMeetingRoom;